import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import updatePromptsInService from '../api/apiPrompts';

const Prompt = ({ selectedPrompt }) => {
  const [promptContent, setPromptContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state for save action
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  useEffect(() => {
    if (selectedPrompt) {
      setPromptContent(selectedPrompt.prompt || ''); // Load the selected prompt content
    }
  }, [selectedPrompt]);

  const handleSave = async () => {
    if (selectedPrompt) {
      setLoading(true); // Start loading spinner
      try {
        const promptDocRef = doc(db, 'prompts', selectedPrompt.id);
        await updateDoc(promptDocRef, { 
          prompt: promptContent, // Update the prompt content
          updated: serverTimestamp() // Set the updated timestamp
        }); 
        //Llama API para refrescarlas en sistema
        if(!updatePromptsInService()){
          console.error('Error updating prompt in service');
          setDialogMessage('Failed to update prompt in service.');
          setShowResultDialog(true);
          return
        }
        setDialogMessage('Prompt updated successfully!');
        setShowResultDialog(true); // Show result dialog
        setIsEditing(false);
      } catch (error) {
        console.error('Error updating prompt:', error);
        setDialogMessage('Failed to update prompt.');
        setShowResultDialog(true);
      }
      setLoading(false); // Stop loading spinner
    }
  };

  const closeResultDialog = () => {
    setShowResultDialog(false);
  };

  if (!selectedPrompt) {
    return <div className="text-gray-500 text-center">Select a prompt to view and edit.</div>;
  }

  return (
    <div className="flex flex-col p-4 w-full bg-white rounded-lg shadow-md overflow-y-auto flex-grow" style={{ minHeight: '0' }}>
      <h2 className="text-lg font-bold mb-4">{selectedPrompt.name} | ID: {selectedPrompt.id}</h2>
      <div className="flex flex-col flex-grow overflow-y-auto">
        <textarea
          className="flex-grow w-full p-2 border border-gray-300 rounded-lg text-sm resize-none overflow-auto"
          value={promptContent}
          onChange={(e) => setPromptContent(e.target.value)}
          disabled={!isEditing}  // Use isEditing to enable/disable editing
          style={{ minHeight: '150px', maxHeight: '700px' }} // Set a smaller height for the textarea
        />
      </div>

      <div className="mt-4 flex justify-end"> {/* Flexbox to align buttons horizontally */}
        {isEditing ? (
          <>
            <button
              className="bg-[#4790CF] text-white px-4 py-2 rounded hover:bg-blue-600 mr-2 flex items-center"
              onClick={handleSave}
              disabled={loading}
            >
              Save
              {loading && (
                <svg
                  className="animate-spin ml-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  ></path>
                </svg>
              )}
            </button>
            <button
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 flex items-center"
              onClick={() => setIsEditing(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>
        )}
      </div>

      {/* Result Dialog */}
      {showResultDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
          <div className="bg-white p-6 rounded-md shadow-lg">
            <p>{dialogMessage}</p>
            <div className="mt-4 flex justify-end">
              <button className="px-4 py-2 bg-blue-500 text-white rounded-md" onClick={closeResultDialog}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prompt;
